/**
 * Lean Theme — Mega Menu
 * Handles desktop hover/keyboard navigation, mobile drawer, sticky header.
 */
(function () {
  'use strict';

  /* ===========================
     DESKTOP MEGA MENU
     =========================== */
  var MegaMenu = {
    hoverDelay: 150,
    closeDelay: 200,
    openTimer: null,
    closeTimer: null,
    activeItem: null,

    init: function () {
      this.navItems = document.querySelectorAll('[data-mega-trigger]');
      if (!this.navItems.length) return;

      this.bindDesktop();
      this.bindKeyboard();
    },

    bindDesktop: function () {
      var self = this;

      this.navItems.forEach(function (item) {
        var menuId = item.getAttribute('aria-controls');
        var menu = menuId ? document.getElementById(menuId) : null;
        if (!menu) return;

        // Hover intent — open
        item.addEventListener('mouseenter', function () {
          clearTimeout(self.closeTimer);
          self.openTimer = setTimeout(function () {
            self.open(item, menu);
          }, self.hoverDelay);
        });

        // Hover leave — close with delay
        item.addEventListener('mouseleave', function () {
          clearTimeout(self.openTimer);
          self.closeTimer = setTimeout(function () {
            self.close(item, menu);
          }, self.closeDelay);
        });

        // Keep open when hovering over the dropdown itself
        menu.addEventListener('mouseenter', function () {
          clearTimeout(self.closeTimer);
        });

        menu.addEventListener('mouseleave', function () {
          self.closeTimer = setTimeout(function () {
            self.close(item, menu);
          }, self.closeDelay);
        });

        // Click support (touch laptops)
        item.addEventListener('click', function (e) {
          if (item.getAttribute('aria-expanded') === 'true') {
            return; // Let the link work if already open
          }
          // Only prevent default if there's a mega menu to show
          if (menu) {
            e.preventDefault();
            self.open(item, menu);
          }
        });
      });

      // Close on click outside
      document.addEventListener('click', function (e) {
        if (!e.target.closest('[data-mega-trigger]') && !e.target.closest('[data-mega-menu]')) {
          self.closeAll();
        }
      });
    },

    bindKeyboard: function () {
      var self = this;

      document.addEventListener('keydown', function (e) {
        // Escape closes any open menu
        if (e.key === 'Escape') {
          self.closeAll();
          if (self.activeItem) {
            self.activeItem.focus();
            self.activeItem = null;
          }
        }
      });

      this.navItems.forEach(function (item) {
        item.addEventListener('keydown', function (e) {
          var menuId = item.getAttribute('aria-controls');
          var menu = menuId ? document.getElementById(menuId) : null;

          if (e.key === 'Enter' || e.key === ' ') {
            if (menu && item.getAttribute('aria-expanded') !== 'true') {
              e.preventDefault();
              self.open(item, menu);
              // Focus first link in mega menu
              var firstLink = menu.querySelector('a, button');
              if (firstLink) firstLink.focus();
            }
          }

          if (e.key === 'ArrowDown') {
            if (menu && item.getAttribute('aria-expanded') === 'true') {
              e.preventDefault();
              firstLink = menu.querySelector('a, button');
              if (firstLink) firstLink.focus();
            }
          }
        });
      });

      // Arrow key navigation within mega menu
      document.querySelectorAll('[data-mega-menu]').forEach(function (menu) {
        menu.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            var links = Array.from(menu.querySelectorAll('a[role="menuitem"], button[role="menuitem"]'));
            var currentIndex = links.indexOf(document.activeElement);

            if (e.key === 'ArrowDown') {
              var next = links[currentIndex + 1] || links[0];
              next.focus();
            } else {
              var prev = links[currentIndex - 1] || links[links.length - 1];
              prev.focus();
            }
          }
        });
      });
    },

    open: function (trigger, menu) {
      // Close any currently open menu first
      this.closeAll();

      trigger.setAttribute('aria-expanded', 'true');
      menu.classList.add('is-open');
      this.activeItem = trigger;
    },

    close: function (trigger, menu) {
      trigger.setAttribute('aria-expanded', 'false');
      menu.classList.remove('is-open');
      if (this.activeItem === trigger) {
        this.activeItem = null;
      }
    },

    closeAll: function () {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);

      this.navItems.forEach(function (item) {
        var menuId = item.getAttribute('aria-controls');
        var menu = menuId ? document.getElementById(menuId) : null;
        item.setAttribute('aria-expanded', 'false');
        if (menu) menu.classList.remove('is-open');
      });
    }
  };

  /* ===========================
     MOBILE MENU
     =========================== */
  var MobileMenu = {
    menu: null,
    previousFocus: null,
    focusableEls: [],

    init: function () {
      this.menu = document.getElementById('MobileMenu');
      if (!this.menu) return;

      this.bindEvents();
    },

    bindEvents: function () {
      var self = this;

      // Open triggers (hamburger button)
      document.querySelectorAll('[data-mobile-menu-open]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.open();
        });
      });

      // Close triggers
      document.querySelectorAll('[data-mobile-menu-close]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.close();
        });
      });

      // Escape key
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.menu.classList.contains('is-open')) {
          self.close();
        }
      });

      // Accordion toggles
      this.menu.querySelectorAll('[data-mobile-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.toggleAccordion(btn);
        });
      });
    },

    open: function () {
      this.previousFocus = document.activeElement;
      this.menu.classList.add('is-open');
      this.menu.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      var panel = this.menu.querySelector('.mobile-menu__panel');
      if (panel) panel.focus();

      this.setupFocusTrap();
    },

    close: function () {
      this.menu.classList.remove('is-open');
      this.menu.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      if (this.previousFocus) {
        this.previousFocus.focus();
      }
    },

    toggleAccordion: function (btn) {
      var targetId = btn.getAttribute('aria-controls');
      var submenu = document.getElementById(targetId);
      if (!submenu) return;

      var isExpanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', !isExpanded);
      submenu.hidden = isExpanded;
    },

    setupFocusTrap: function () {
      var panel = this.menu.querySelector('.mobile-menu__panel');
      if (!panel) return;

      this.menu.addEventListener('keydown', function handler(e) {
        if (e.key !== 'Tab') return;

        var focusable = panel.querySelectorAll(
          'a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;

        var first = focusable[0];
        var last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });
    }
  };

  /* ===========================
     STICKY HEADER
     =========================== */
  var StickyHeader = {
    header: null,
    placeholder: null,
    lastScroll: 0,
    headerHeight: 0,
    isSticky: false,
    behavior: 'always', // 'always', 'scroll_up', 'none'

    init: function () {
      this.header = document.querySelector('[data-sticky-header]');
      if (!this.header) return;

      this.behavior = this.header.getAttribute('data-sticky-behavior') || 'always';
      if (this.behavior === 'none') return;

      this.headerHeight = this.header.offsetHeight;
      this.createPlaceholder();

      var self = this;
      var ticking = false;

      window.addEventListener('scroll', function () {
        if (!ticking) {
          requestAnimationFrame(function () {
            self.onScroll();
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });

      // Update height on resize
      window.addEventListener('resize', function () {
        self.headerHeight = self.header.offsetHeight;
        if (self.placeholder) {
          self.placeholder.style.height = self.headerHeight + 'px';
        }
      }, { passive: true });
    },

    createPlaceholder: function () {
      this.placeholder = document.createElement('div');
      this.placeholder.className = 'header-sticky-placeholder';
      this.placeholder.style.height = this.headerHeight + 'px';
      this.placeholder.style.display = 'none';
      this.header.parentNode.insertBefore(this.placeholder, this.header.nextSibling);
    },

    onScroll: function () {
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (this.behavior === 'always') {
        if (scrollTop > this.headerHeight) {
          this.stick();
        } else {
          this.unstick();
        }
      } else if (this.behavior === 'scroll_up') {
        if (scrollTop > this.headerHeight) {
          if (scrollTop < this.lastScroll) {
            // Scrolling up
            this.stick();
            this.header.classList.remove('header--hidden');
          } else {
            // Scrolling down
            if (this.isSticky) {
              this.header.classList.add('header--hidden');
            }
          }
        } else {
          this.unstick();
          this.header.classList.remove('header--hidden');
        }
      }

      this.lastScroll = scrollTop;
    },

    stick: function () {
      if (this.isSticky) return;
      this.isSticky = true;
      this.header.classList.add('header--sticky');
      this.placeholder.style.display = 'block';
    },

    unstick: function () {
      if (!this.isSticky) return;
      this.isSticky = false;
      this.header.classList.remove('header--sticky');
      this.header.classList.remove('header--hidden');
      this.placeholder.style.display = 'none';
    }
  };

  /* ===========================
     INIT
     =========================== */
  function init() {
    MegaMenu.init();
    MobileMenu.init();
    StickyHeader.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external use
  window.LeanMegaMenu = MegaMenu;
  window.LeanMobileMenu = MobileMenu;
})();
